import React from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.bealyo.bealyoapp";

//const APP_STORE_URL = "PASTE_YOUR_APP_STORE_URL_HERE";
const APP_STORE_URL = "https://apps.apple.com/app/id6773874077";

const PageContainer = styled.div`
  min-height: 100vh;
  padding: 0px 20px 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #e5e7eb;
  background: #080f1c;
  font-family: Inter, "Segoe UI", Arial, sans-serif;
  background-attachment: fixed;

  &,
  & * {
    box-sizing: border-box;
  }

  button,
  a {
    -webkit-tap-highlight-color: transparent;
  }

  button {
    font-family: inherit;
  }
`;

const LogoWrap = styled.div`
  margin-bottom: 22px;

  img {
    height: 58px;
    width: auto;
    display: block;
    object-fit: contain;
  }

  @media (max-width: 600px) {
    img {
      height: 48px;
    }
  }
`;

const SectionTitle = styled.section`
  max-width: 760px;
  text-align: center;
  margin: 28px auto 22px;

  h2 {
    font-size: 34px;
    font-weight: 800;
    margin-bottom: 10px;
  }

  p {
    color: rgba(226, 232, 240, 0.78);
    font-size: 17px;
    line-height: 1.6;
  }
`;

const FeatureGrid = styled.section`
  max-width: 1100px;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
  margin: 0 auto 44px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureBox = styled.div`
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.95),
    rgba(15, 23, 42, 0.74)
  );
  border: 1px solid rgba(148, 163, 184, 0.36);
  border-radius: 22px;
  padding: 22px;
  min-height: 190px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.35);

  div {
    font-size: 30px;
    margin-bottom: 12px;
  }

  h3 {
    font-size: 19px;
    margin-bottom: 8px;
  }

  p {
    color: rgba(226, 232, 240, 0.78);
    line-height: 1.55;
    font-size: 14px;
  }
`;

const Hero = styled.section`
  width: 100%;
  max-width: 1100px;
  margin: 0 auto 32px;
  padding: 16px 0 28px;

  display: grid;
  grid-template-columns: minmax(0, 1fr) 220px;
  align-items: start;
  gap: 56px;

  background:
    radial-gradient(
      ellipse at 85% 45%,
      rgba(0, 125, 244, 0.14),
      transparent 60%
    ),
    #080f1c;

  border-bottom: 1px solid rgba(148, 163, 184, 0.14);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 0 0 24px;
    gap: 28px;
  }
`;

const HeroLeft = styled.div`
  min-width: 0;
`;

const HeroRight = styled.div`
  min-width: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Pill = styled.div`
  display: inline-block;
  padding: 7px 16px;
  border-radius: 999px;

  background: rgba(0, 125, 244, 0.12);
  border: 1px solid rgba(0, 125, 244, 0.45);

  color: #007df4;

  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 18px;
`;

const FinalCTA = styled.section`
  max-width: 760px;
  width: 100%;
  text-align: center;
  margin: 20px auto 42px;
  padding: 34px 28px;
  border-radius: 28px;
  background: #0d1727;
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: none;

  h2 {
    font-size: 26px;
    font-weight: 800;
    margin-bottom: 10px;
  }

  p {
    color: rgba(226, 232, 240, 0.86);
    font-size: 17px;
    line-height: 1.65;
    margin-bottom: 24px;
  }
`;

const Title = styled.h1`
  font-size: 56px;
  font-weight: 800;
  margin-bottom: 18px;
  text-shadow: 0 8px 24px rgba(0, 0, 0, 0.7);

  @media (max-width: 600px) {
    font-size: 40px;
  }
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: rgba(226, 232, 240, 0.85);
  max-width: 620px;
  margin: 0 0 28px;
  line-height: 1.6;

  @media (max-width: 600px) {
    font-size: 16px;
  }
`;

const AudienceCTA = styled.div`
  display: flex;
  gap: 10px;
  margin: 24px 0 0;
  max-width: 620px;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const AudienceCard = styled.a`
  flex: 1;
  min-width: 0;
  padding: 16px;
  border-radius: 10px;

  text-decoration: none;
  color: #ffffff;
  background: #0d1727;
  border: 1px solid rgba(148, 163, 184, 0.18);

  transition:
    background 0.18s ease,
    border-color 0.18s ease;

  &:hover {
    background: #122039;
    border-color: rgba(96, 165, 250, 0.5);
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid #8bb8ff;
    outline-offset: 3px;
  }

  strong {
    display: block;
    margin-bottom: 6px;
    font-size: 14px;
    font-weight: 600;
  }

  span {
    display: block;
    color: #aebbd0;
    font-size: 13px;
    line-height: 1.5;
  }
`;

const StepsCard = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 16px auto 48px;
  padding: 32px;

  background: #0d1727;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 16px;

  > p {
    max-width: 720px;
    color: #aebbd0;
    font-size: 16px;
    line-height: 1.7;
  }

  @media (max-width: 600px) {
    padding: 24px 20px;
  }
`;

const DownloadTitle = styled.h2`
  font-size: 24px;
  margin-bottom: 16px;
  text-align: left;
`;

const Steps = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0 0 22px;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 6px 0;
    font-size: 15px;
    color: rgba(226, 232, 240, 0.92);
  }
`;

const StepNumber = styled.span`
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
`;

const DownloadButton = styled.a`
  width: 220px;
  height: 70px;

  display: flex;
  align-items: center;
  justify-content: center;

  text-decoration: none;

  transition:
    transform 0.15s ease,
    opacity 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    opacity: 0.92;
    text-decoration: none;
  }

  img {
    display: block;
    width: auto;
    height: 52px;
    object-fit: contain;
  }

  @media (max-width: 600px) {
    width: 210px;
    height: 60px;

    img {
      height: 48px;
    }
    .google-play-badge {
      height: 44px;
    }
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 24px;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 4px;
  }
`;

const SmallNote = styled.p`
  margin-top: 12px;
  font-size: 13px;
  color: rgba(148, 163, 184, 0.88);
  line-height: 1.5;
`;

/* ---- PHONE + CAROUSEL ---- */

const PhoneCarousel = styled.div`
  position: relative;
  width: 220px;
  height: 440px;

  @media (max-width: 600px) {
    width: 200px;
    height: 400px;
  }
`;

const PhoneMock = styled.div`
  width: 100%;
  height: 100%;
  padding: 8px;
  border-radius: 30px;
  background: #101827;
  border: 1px solid rgba(148, 163, 184, 0.4);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
`;

const PhoneScreen = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 28px;
  padding: 18px 14px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: #e5e7eb;
  font-size: 13px;
  background: ${(props) => {
    if (props.variant === "gps") {
      return "radial-gradient(circle at top, #3b82f6, #0f172a 45%, #020617 100%)";
    }
    if (props.variant === "community") {
      return "radial-gradient(circle at top, #a855f7, #0f172a 45%, #020617 100%)";
    }
    return "radial-gradient(circle at top, #22c55e, #0f172a 45%, #020617 100%)";
  }};
`;

const PhoneHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
`;

const PhoneChip = styled.div`
  padding: 4px 9px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.8);
  font-size: 11px;
`;

const PhoneCard = styled.div`
  background: rgba(15, 23, 42, 0.9);
  border-radius: 16px;
  padding: 10px 12px;
  margin-bottom: 8px;
  border: 1px solid rgba(148, 163, 184, 0.35);
`;

const Feedback = styled.footer`
  max-width: 1100px;
  width: 100%;
  margin: 36px auto 0;
  padding: 26px 28px 20px;
  border-top: 1px solid rgba(148, 163, 184, 0.24);
  color: rgba(226, 232, 240, 0.82);
`;

const FooterTop = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr;
  gap: 28px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 22px;
    text-align: center;
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  h4 {
    font-size: 13px;
    margin: 0 0 4px;
    color: #ffffff;
    font-weight: 900;
  }

  a,
  button {
    border: none;
    background: transparent;
    padding: 0;
    color: rgba(226, 232, 240, 0.68);
    text-decoration: none;
    font-size: 14px;
    line-height: 1.4;
    text-align: left;
    cursor: pointer;
    font-family: inherit;
  }

  a:hover,
  button:hover {
    color: #007df4;
  }

  @media (max-width: 760px) {
    align-items: center;

    a,
    button {
      text-align: center;
    }
  }
`;

const PageHero = styled.div`
  max-width: 820px;
  margin: 0px 0 20px;

  h1 {
    font-size: 42px;
    font-weight: 900;
    margin: 0 0 14px;
    line-height: 1.15;
    color: #ffffff;
    text-shadow: 0 8px 24px rgba(0, 0, 0, 0.55);
  }

  p {
    margin: 0;
    font-size: 18px;
    line-height: 1.65;
    color: rgba(226, 232, 240, 0.86);
  }

  @media (max-width: 600px) {
    h1 {
      font-size: 32px;
    }

    p {
      font-size: 16px;
    }
  }
`;

const HomeHeroCopy = styled.div`
  min-width: 0;
  max-width: 660px;

  && > h1 {
    margin: 0 0 20px;
    font-family: inherit;
    font-size: clamp(38px, 4.4vw, 56px);
    line-height: 1.08;
    font-weight: 800;
    letter-spacing: -0.035em;
    color: #ffffff;
    text-shadow: none;
  }

  && > h1 > span {
    color: #49a6ff;
  }

  && > p {
    max-width: 520px;
    margin: 0;
    font-family: inherit;
    font-size: 17px;
    line-height: 1.7;
    color: #aebbd0;
  }

  @media (max-width: 600px) {
    && > h1 {
      font-size: 36px;
    }

    && > p {
      font-size: 16px;
    }
  }
`;

const FooterBottom = styled.div`
  margin-top: 22px;
  padding-top: 16px;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
  font-size: 12px;
  color: rgba(148, 163, 184, 0.72);

  @media (max-width: 760px) {
    text-align: center;
  }
`;

/* Carousel controls */

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: rgba(15, 23, 42, 0.95);
  width: 30px;
  height: 30px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e5e7eb;
  cursor: pointer;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.7);
  font-size: 16px;
  padding: 0;

  &:hover {
    background: rgba(30, 64, 175, 0.98);
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

const NavPrev = styled(NavButton)`
  left: -18px;

  @media (max-width: 600px) {
    left: -6px;
  }
`;

const NavNext = styled(NavButton)`
  right: -18px;

  @media (max-width: 600px) {
    right: -6px;
  }
`;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 14px;
`;

const StatsRow = styled.section`
  max-width: 1100px;
  width: 100%;
  margin: 0 auto 36px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const StatCard = styled.div`
  background: rgba(15, 23, 42, 0.82);
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 18px;
  padding: 16px;
  backdrop-filter: blur(14px);

  strong {
    display: block;
    font-size: 18px;
    margin-bottom: 5px;
  }

  span {
    color: rgba(226, 232, 240, 0.78);
    font-size: 13px;
    line-height: 1.45;
  }
`;

const Dot = styled.button`
  width: ${(props) => (props.active ? "14px" : "8px")};
  height: 8px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  background: ${(props) =>
    props.active ? "#007df4" : "rgba(148, 163, 184, 0.7)"};
  opacity: ${(props) => (props.active ? 1 : 0.6)};
  padding: 0;
`;

/* ---- REST OF THE PAGE ---- */

const WhyRow = styled.section`
  max-width: 1100px;
  width: 100%;
  margin: 0 auto 40px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const WhyCard = styled.div`
  background: rgba(15, 23, 42, 0.95);
  border-radius: 18px;
  padding: 18px 20px;
  border: 1px solid rgba(148, 163, 184, 0.45);

  h3 {
    font-size: 17px;
    margin-bottom: 6px;
  }

  p {
    font-size: 14px;
    color: rgba(209, 213, 219, 0.9);
    line-height: 1.55;
  }
`;

const Features = styled.section`
  max-width: 900px;
  width: 100%;
  margin: 10px auto 36px;
  text-align: center;

  h3 {
    font-size: 22px;
    margin-bottom: 14px;
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  li {
    margin: 6px 0;
    font-size: 16px;
    color: rgba(226, 232, 240, 0.9);
  }
`;

const FooterSection = styled.footer`
  max-width: 1100px;
  width: 100%;
  margin: 20px auto 0;
`;

const FooterCard = styled.div`
  padding: 24px 28px;
  border-radius: 30px;
  text-align: center;
  background:
    radial-gradient(
      circle at top left,
      rgba(56, 189, 248, 0.18),
      transparent 55%
    ),
    radial-gradient(
      circle at bottom right,
      rgba(249, 115, 22, 0.2),
      transparent 55%
    ),
    rgba(15, 23, 42, 0.94);
  border: 1px solid rgba(148, 163, 184, 0.34);
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.45);
`;

const FooterBrand = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  img {
    height: 34px;
    width: auto;
    display: block;
    margin-bottom: 10px;
  }

  p {
    font-size: 14px;
    color: rgba(226, 232, 240, 0.68);
    line-height: 1.5;
    margin: 0;
  }

  @media (max-width: 760px) {
    align-items: center;
    text-align: center;
  }
`;

const FooterTitle = styled.h2`
  font-size: 22px;
  font-weight: 900;
  margin-bottom: 12px;

  @media (max-width: 600px) {
    font-size: 26px;
  }
`;

const FooterText = styled.p`
  max-width: 680px;
  margin: 0 auto 24px;
  color: rgba(226, 232, 240, 0.82);
  font-size: 16px;
  line-height: 1.65;
`;

const FooterContact = styled.div`
  margin: 0 auto 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: rgba(226, 232, 240, 0.74);

  a {
    color: #ffffff;
    font-weight: 900;
    font-size: 17px;
    text-decoration: none;
  }

  a:hover {
    color: #007df4;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 24px;

  a {
    padding: 8px 14px;
    min-width: 90px;
    font-size: 14px;
    border-radius: 999px;
    color: #e5e7eb;
    text-decoration: none;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(148, 163, 184, 0.28);
    font-weight: 800;
  }

  a:hover {
    background: rgba(249, 115, 22, 0.18);
    border-color: rgba(249, 115, 22, 0.45);
  }
`;

const CommunityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 22px;
  margin-top: 40px;
  margin-bottom: 25px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 26px;
  }
`;

const CommunityCard = styled.div`
  overflow: hidden;
  border-radius: 24px;
  background: rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.22);
`;

const CommunityImage = styled.img`
  width: 100%;
  height: 170px;
  object-fit: cover;
  display: block;

  @media (max-width: 600px) {
    height: 150px;
  }
`;

const CommunityContent = styled.div`
  padding: 18px;

  strong {
    display: none;
  }

  h3 {
    margin: 0 0 8px;
    font-size: 20px;
    color: #ffffff;
  }

  p {
    margin: 0;
    color: rgba(226, 232, 240, 0.78);
    line-height: 1.5;
    font-size: 14px;
  }
`;

const CommunityLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;

  margin-top: 12px;

  color: #60a5fa;
  font-size: 13px;
  font-weight: 800;
  text-decoration: none;

  transition:
    color 0.18s ease,
    transform 0.18s ease;

  &:hover {
    color: #93c5fd;
    transform: translateX(2px);
    text-decoration: none;
  }
`;

const BestHero = styled.section`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(300px, 0.9fr);
  gap: 46px;
  align-items: start;
  margin-bottom: 54px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const BestHeroCopy = styled.div`
  h1 {
    max-width: 720px;
    margin: 0 0 18px;
    color: #ffffff;
    font-size: 48px;
    line-height: 1.08;
    font-weight: 900;
    text-shadow: 0 8px 24px rgba(0, 0, 0, 0.55);
  }

  p {
    max-width: 700px;
    margin: 0;
    color: rgba(226, 232, 240, 0.84);
    font-size: 18px;
    line-height: 1.7;
  }

  @media (max-width: 600px) {
    h1 {
      font-size: 35px;
    }

    p {
      font-size: 16px;
    }
  }
`;

const BestHeroVisual = styled.div`
  min-height: 390px;
  border-radius: 30px;
  overflow: hidden;
  position: relative;

  background:
    linear-gradient(
      180deg,
      rgba(2, 6, 23, 0.05) 25%,
      rgba(2, 6, 23, 0.95) 100%
    ),
    url("/earlyaccess/beyourbest1.png") center / cover no-repeat;

  border: 1px solid rgba(148, 163, 184, 0.3);
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.55);

  @media (max-width: 900px) {
    min-height: 330px;
  }
`;

const BestVisualText = styled.div`
  position: absolute;
  left: 24px;
  right: 24px;
  bottom: 24px;

  strong {
    display: block;
    margin-bottom: 7px;
    color: #ffffff;
    font-size: 22px;
  }

  span {
    color: rgba(226, 232, 240, 0.78);
    font-size: 14px;
    line-height: 1.5;
  }
`;

const BestActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 28px;
`;

const PrimaryAction = styled.button`
  min-height: 48px;
  padding: 0 22px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  border: none;
  cursor: pointer;
  font-family: inherit;

  background: #007df4;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;

  transition:
    transform 0.18s ease,
    background 0.18s ease;

  &:hover {
    transform: translateY(-2px);
    background: #168cff;
  }
`;

const SecondaryAction = styled.button`
  min-height: 48px;
  padding: 0 22px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  color: #e5e7eb;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(148, 163, 184, 0.32);

  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.11);
  }
`;

const BestJourney = styled.section`
  width: 100%;
  margin: 48px auto 52px;
  padding: 30px;
  border-radius: 28px;

  background:
    radial-gradient(
      circle at top left,
      rgba(0, 125, 244, 0.2),
      transparent 42%
    ),
    radial-gradient(
      circle at bottom right,
      rgba(168, 85, 247, 0.2),
      transparent 44%
    ),
    rgba(15, 23, 42, 0.86);

  border: 1px solid rgba(148, 163, 184, 0.26);

  h2 {
    margin: 0 0 12px;
    color: #ffffff;
    font-size: 28px;
  }

  > p {
    max-width: 760px;
    margin: 0;
    color: rgba(226, 232, 240, 0.8);
    line-height: 1.65;
    font-size: 16px;
  }

  @media (max-width: 600px) {
    padding: 22px;
  }
`;

const JourneySteps = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-top: 26px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const JourneyCard = styled.div`
  padding: 18px;
  border-radius: 18px;
  background: rgba(2, 6, 23, 0.48);
  border: 1px solid rgba(148, 163, 184, 0.2);

  span {
    display: block;
    margin-bottom: 10px;
    font-size: 26px;
  }

  h3 {
    margin: 0 0 7px;
    color: #ffffff;
    font-size: 17px;
  }

  p {
    margin: 0;
    color: rgba(226, 232, 240, 0.72);
    font-size: 13px;
    line-height: 1.5;
  }
`;

/* ---- PHONE SCREEN DATA ---- */

const phoneScreensOld = [
  {
    id: "daily",
    variant: "daily",
    header: "Today • 5.2 km",
    chip: "Streak x4 🔥",
    footer: "Track your activities, habits and wins in one place.",
    body: () => (
      <div style={{ marginTop: 12 }}>
        <PhoneCard>
          <strong>Morning Run</strong>
          <div>Park • 32 min • 4.1 km</div>
        </PhoneCard>
        <PhoneCard>
          <strong>Cold Shower Legend</strong>
          <div>Task completed • +15 XP</div>
        </PhoneCard>
        <PhoneCard>
          <strong>Hydration Hero</strong>
          <div>8/8 cups logged</div>
        </PhoneCard>
      </div>
    ),
  },
  {
    id: "gps",
    variant: "gps",
    header: "Saturday Run • 7.8 km",
    chip: "Route complete ✅",
    footer: "See every km, pace and route with live GPS tracking.",
    body: () => (
      <div style={{ marginTop: 12 }}>
        <PhoneCard style={{ marginBottom: 10 }}>
          <strong>City Loop Route</strong>
          <div>7.8 km • 5:42 /km • 44 min</div>
        </PhoneCard>
        <PhoneCard>
          <div style={{ fontSize: 11, opacity: 0.9 }}>
            Split times, pace zones and elevation - all in one clean view.
          </div>
        </PhoneCard>
      </div>
    ),
  },
  {
    id: "community",
    variant: "community",
    header: "Circle • Morning Legends",
    chip: "8 friends active now",
    footer: "Stay accountable with circles, cheers and shared wins.",
    body: () => (
      <div style={{ marginTop: 12 }}>
        <PhoneCard>
          <strong>Noa just crushed “5K & Proud”</strong>
          <div>🔥🔥🔥 • 17 cheers</div>
        </PhoneCard>
        <PhoneCard>
          <strong>You</strong>
          <div>Cold Shower Legend • Day 4 / 7</div>
        </PhoneCard>
        <PhoneCard>
          <div>“Let’s hit 10k steps together today 💪”</div>
        </PhoneCard>
      </div>
    ),
  },
];

const InfoPage = styled.section`
  max-width: 1100px;
  width: 100%;
  margin: 0 auto 60px;
`;

const PageBadge = styled.div`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 999px;
  background: rgba(0, 125, 244, 0.16);
  color: #007df4;
  border: 1px solid rgba(0, 125, 244, 0.35);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 18px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 22px;
  margin-top: 40px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  padding: 28px;
  border-radius: 24px;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.96),
    rgba(15, 23, 42, 0.82)
  );
  border: 1px solid rgba(148, 163, 184, 0.28);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.35);

  strong {
    display: block;
    font-size: 42px;
    margin-bottom: 18px;
  }

  h3 {
    margin: 0 0 10px;
    font-size: 24px;
    color: white;
  }

  p {
    margin: 0;
    color: rgba(226, 232, 240, 0.8);
    line-height: 1.7;
    font-size: 15px;
  }
`;

const phoneScreens = [
  {
    id: "daily",
    variant: "daily",
    header: "Daily wins & streaks",
    footer: "Track your activities, habits and wins in one place.",
    image: "/earlyaccess/home1dark@2x.png",
  },
  {
    id: "gps",
    variant: "gps",
    header: "GPS running & walking",
    footer: "See every km, pace and route with live GPS tracking.",
    image: "/earlyaccess/home2dark@2x.png",
  },
  {
    id: "leaderboards",
    variant: "leaderboards",
    header: "Leaderboards & rankings",
    footer: "Compete with friends and climb the leaderboard together.",
    image: "/earlyaccess/homeleader@2x.png",
  },
  {
    id: "community",
    variant: "community",
    header: "Circles & community",
    footer: "Stay accountable with circles, cheers and shared wins.",
    image: "/earlyaccess/home4dark@2x.png",
  },
];

const PhoneImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 23px;
  object-fit: cover;
  display: block;
`;

const SiteHeader = styled.header`
  width: calc(100% + 40px);
  margin: 0 -20px 24px;
  padding: 0 20px;
  position: sticky;
  top: 0;
  z-index: 20;

  background: rgba(8, 15, 28, 0.96);
  border-bottom: 1px solid rgba(148, 163, 184, 0.14);
  backdrop-filter: blur(18px);

  @media (max-width: 760px) {
    margin-bottom: 0;
  }
`;

const HeaderInner = styled.div`
  width: 100%;
  max-width: 1100px;
  min-height: 68px;
  margin: 0 auto;
  padding: 0;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 760px) {
    min-height: 58px;
  }
`;

const HeaderLogo = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;

  img {
    display: block;
    width: auto;
    height: 35px;
    max-width: 110px;
    object-fit: contain;
  }
`;

const DesktopNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 760px) {
    display: none;
  }
`;

const NavLink = styled.button`
  border: none;
  cursor: pointer;
  padding: 10px 16px;
  border-radius: 999px;
  color: #e5e7eb;

  background: ${(props) =>
    props.active ? "rgba(0,125,244,0.18)" : "transparent"};

  border: 1px solid
    ${(props) => (props.active ? "rgba(0,125,244,0.45)" : "transparent")};

  font-weight: 700;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 125, 244, 0.12);
    color: white;
  }
`;

const MenuButton = styled.button`
  display: none;
  border: none;
  background: rgba(255, 255, 255, 0.08);
  color: #e5e7eb;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  font-size: 24px;
  cursor: pointer;

  @media (max-width: 760px) {
    display: block;
  }
`;

const MobileMenu = styled.div`
  margin-top: 0;
  padding: 8px;
  border-radius: 0 0 16px 16px;
  background: rgba(15, 23, 42, 0.98);
  border: 1px solid rgba(148, 163, 184, 0.26);
  border-top: none;
  display: grid;
  gap: 5px;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.42);
`;

const MobileNavLink = styled.button`
  width: 100%;
  text-align: left;
  border: none;
  cursor: pointer;
  padding: 11px 13px;
  border-radius: 12px;
  color: #e5e7eb;

  background: ${(props) =>
    props.active ? "rgba(0, 125, 244, 0.18)" : "transparent"};

  border: 1px solid
    ${(props) => (props.active ? "rgba(0, 125, 244, 0.36)" : "transparent")};

  font-weight: 500;
  font-size: 14px;
  font-family: inherit;

  transition:
    background 0.18s ease,
    border-color 0.18s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
`;

const StepsGrid = styled.div`
  max-width: 1100px;
  width: 100%;
  margin: 34px auto 0;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const SectionHeader = styled.div`
  margin-bottom: 28px;

  h2 {
    margin: 0;
    margin-top: 10px;
    font-size: 24px;
    font-weight: 800;
    line-height: 1.2;
    color: #fff;
  }

  p {
    margin: 12px 0 0;
    font-size: 18px;
    line-height: 1.6;
    color: rgba(226, 232, 240, 0.82);
    max-width: 720px;
  }

  @media (max-width: 768px) {
    h2 {
      font-size: 28px;
    }

    p {
      font-size: 16px;
    }
  }
`;

const StepCard = styled.div`
  padding: 20px;
  border-radius: 20px;
  background: rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.22);

  h3 {
    font-size: 18px;
    margin: 12px 0 8px;
    color: #ffffff;
  }

  p {
    margin: 0;
    color: rgba(226, 232, 240, 0.76);
    font-size: 14px;
    line-height: 1.5;
  }
`;

const StepCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(249, 115, 22, 0.16);
  border: 1px solid rgba(249, 115, 22, 0.38);
  color: #fed7aa;
  font-weight: 900;
  font-size: 14px;
`;

// ---- PROFESSIONAL B2B HEADER ----

const HeaderNavItem = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #d5deed;
  padding: 8px 10px;
  font-size: 13px;
  line-height: 1.4;
  font-weight: 500;
  font-family: inherit;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  border-radius: 8px;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.06);
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid #8bb8ff;
    outline-offset: 3px;
  }
`;

const SolutionsWrap = styled.div`
  position: relative;
`;

const SolutionsDropdown = styled.div`
  position: absolute;
  top: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%);

  width: 390px;
  padding: 10px;

  background: rgba(15, 23, 42, 0.98);
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 18px;

  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(20px);

  z-index: 100;
`;

const SolutionItem = styled.button`
  width: 100%;
  display: block;
  text-align: left;

  padding: 14px 15px;
  border: none;
  border-radius: 12px;

  background: transparent;
  color: #ffffff;
  cursor: pointer;
  font-family: inherit;

  transition: background 0.18s ease;

  &:hover {
    background: rgba(0, 125, 244, 0.12);
  }

  strong {
    display: block;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 4px;
  }

  span {
    display: block;
    font-size: 12px;
    line-height: 1.4;
    color: rgba(226, 232, 240, 0.65);
  }
`;

const DemoButton = styled.button`
  min-height: 40px;
  padding: 0 16px;

  border: none;
  border-radius: 9px;

  background: #007df4;
  color: #ffffff;

  font-family: inherit;
  font-size: 13px;
  font-weight: 600;

  cursor: pointer;
  white-space: nowrap;

  box-shadow: 0 10px 30px rgba(0, 125, 244, 0.22);

  transition:
    transform 0.18s ease,
    background 0.18s ease,
    box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    background: #168cff;
    box-shadow: 0 14px 36px rgba(0, 125, 244, 0.32);
  }
`;

const MobileNavSection = styled.div`
  padding: 4px 6px;

  > strong {
    display: block;
    padding: 5px 7px 8px;
    color: rgba(148, 163, 184, 0.75);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
`;

const MobileSolutionItem = styled.button`
  width: 100%;
  display: block;
  text-align: left;

  padding: 11px 12px;
  margin-bottom: 3px;

  border: none;
  border-radius: 10px;
  background: transparent;

  color: #ffffff;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: rgba(0, 125, 244, 0.12);
  }

  strong {
    display: block;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 2px;
  }

  span {
    display: block;
    font-size: 11px;
    line-height: 1.4;
    color: rgba(226, 232, 240, 0.62);
  }
`;

const MobileDemoButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  min-height: 44px;
  margin-top: 18px;
  padding: 10px 18px;

  border: none;
  border-radius: 10px;
  background: #007df4;
  color: #fff;

  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: 0;
  text-shadow: none;
  cursor: pointer;

  &:hover {
    background: #168cff;
  }
`;

const ProductHome = () => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [activePage, setActivePage] = React.useState(() => {
    const hash = window.location.hash.replace("#", "");

    if (["home", "best", "community", "contact"].includes(hash)) {
      return hash;
    }

    return "home";
  });
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [solutionsOpen, setSolutionsOpen] = React.useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = React.useState(false);

  const active = phoneScreens[activeIndex];

  const pages = [
    { id: "home", label: "Home" },
    { id: "best", label: "Be Your Best" },
    { id: "community", label: "Start a Community" },
    { id: "contact", label: "Contact" },
  ];

  const goToPage = (pageId) => {
    setActivePage(pageId);
    setMenuOpen(false);
    setSolutionsOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goPrev = () => {
    setActiveIndex((prev) => (prev === 0 ? phoneScreens.length - 1 : prev - 1));
  };

  const goNext = () => {
    setActiveIndex((prev) => (prev === phoneScreens.length - 1 ? 0 : prev + 1));
  };

  return (
    <PageContainer>
      <SiteHeader>
        <HeaderInner menuOpen={menuOpen}>
          <HeaderLogo onClick={() => goToPage("home")}>
            <img src="/logo.png" alt="Bealyo" />
          </HeaderLogo>

          <DesktopNav>
            <HeaderNavItem onClick={() => goToPage("home")}>Home</HeaderNavItem>

            <SolutionsWrap>
              <HeaderNavItem onClick={() => setSolutionsOpen((prev) => !prev)}>
                Solutions {solutionsOpen ? "▲" : "▼"}
              </HeaderNavItem>

              {solutionsOpen && (
                <SolutionsDropdown>
                  <SolutionItem
                    onClick={() => {
                      window.location.href = "/gyms";
                    }}
                  >
                    <strong>Gyms & Health Clubs</strong>
                    <span>Challenges, community & live leaderboards</span>
                  </SolutionItem>

                  <SolutionItem
                    onClick={() => {
                      setSolutionsOpen(false);
                      window.location.href = "/gyms";
                    }}
                  >
                    <strong>Gym Chains</strong>
                    <span>Engagement across multiple locations</span>
                  </SolutionItem>

                  <SolutionItem
                    onClick={() => {
                      setSolutionsOpen(false);
                      goToPage("community");
                    }}
                  >
                    <strong>Trainers & Communities</strong>
                    <span>Build and engage your community</span>
                  </SolutionItem>

                  <SolutionItem
                    onClick={() => {
                      setSolutionsOpen(false);
                      goToPage("best");
                    }}
                  >
                    <strong>Individuals</strong>
                    <span>Be the best version of yourself</span>
                  </SolutionItem>
                </SolutionsDropdown>
              )}
            </SolutionsWrap>

            <HeaderNavItem onClick={() => goToPage("community")}>
              Communities
            </HeaderNavItem>

            <HeaderNavItem onClick={() => goToPage("contact")}>
              Contact
            </HeaderNavItem>

            <HeaderNavItem
              as="a"
              href="/admin"
              style={{
                textDecoration: "none",
                border: "1px solid rgba(148, 163, 184, 0.4)",
                borderRadius: 8,
                padding: "7px 12px",
                marginLeft: 10,
                marginRight: 12,
              }}
            >
              {isAuthenticated ? "My Community" : "Log in"}
            </HeaderNavItem>

            <DemoButton onClick={() => goToPage("contact")}>
              Book a Demo
            </DemoButton>
          </DesktopNav>

          <MenuButton onClick={() => setMenuOpen((prev) => !prev)}>
            {menuOpen ? "×" : "☰"}
          </MenuButton>
        </HeaderInner>

        {menuOpen && (
          <MobileMenu>
            <MobileNavSection>
              <MobileNavLink onClick={() => goToPage("home")}>
                Home
              </MobileNavLink>

              <MobileNavLink
                onClick={() => setMobileSolutionsOpen((prev) => !prev)}
              >
                Solutions {mobileSolutionsOpen ? "▲" : "▼"}
              </MobileNavLink>
              {mobileSolutionsOpen && (
                <>
                  <MobileSolutionItem
                    onClick={() => {
                      window.location.href = "/gyms";
                    }}
                  >
                    <strong>Gyms & Health Clubs</strong>
                    <span>Challenges, community & live leaderboards</span>
                  </MobileSolutionItem>

                  <MobileSolutionItem
                    onClick={() => {
                      window.location.href = "/gyms";
                    }}
                  >
                    <strong>Gym Chains</strong>
                    <span>Engagement across multiple locations</span>
                  </MobileSolutionItem>

                  <MobileSolutionItem onClick={() => goToPage("community")}>
                    <strong>Trainers & Communities</strong>
                    <span>Build and engage your community</span>
                  </MobileSolutionItem>

                  <MobileSolutionItem onClick={() => goToPage("best")}>
                    <strong>Individuals</strong>
                    <span>Be the best version of yourself</span>
                  </MobileSolutionItem>
                </>
              )}

              <MobileNavLink onClick={() => goToPage("community")}>
                Communities
              </MobileNavLink>

              <MobileNavLink onClick={() => goToPage("contact")}>
                Contact
              </MobileNavLink>

              <MobileNavLink
                as="a"
                href="/admin"
                onClick={() => setMenuOpen(false)}
                style={{
                  textDecoration: "none",
                  border: "1px solid rgba(148, 163, 184, 0.4)",
                  borderRadius: 8,
                  padding: "7px 12px",
                  marginLeft: 10,
                  marginRight: 12,
                }}
              >
                {isAuthenticated ? "My Community" : "Log in"}
              </MobileNavLink>

              <MobileDemoButton onClick={() => goToPage("contact")}>
                Book a Demo
              </MobileDemoButton>
            </MobileNavSection>
          </MobileMenu>
        )}
      </SiteHeader>
      {activePage === "home" && (
        <>
          <Hero>
            <HeroLeft>
              <HomeHeroCopy>
                <PageBadge>Community Engagement Platform</PageBadge>

                <h1>
                  Build a Community
                  <br />
                  <span>That Keeps Moving.</span>
                </h1>

                <p>
                  Bring people together through challenges, live leaderboards,
                  and shared progress. Bealyo helps gyms and communities create
                  more reasons to participate and stay connected.
                </p>

                <PrimaryAction
                  type="button"
                  onClick={() => goToPage("contact")}
                  style={{ marginTop: 28 }}
                >
                  Book a Demo
                </PrimaryAction>

                <AudienceCTA>
                  <AudienceCard href="/gyms">
                    <strong>For Gyms & Health Clubs →</strong>
                    <span>
                      Live leaderboards, challenges & member engagement.
                    </span>
                  </AudienceCard>

                  <AudienceCard
                    href="#community"
                    onClick={(e) => {
                      e.preventDefault();
                      goToPage("community");
                    }}
                  >
                    <strong>For Communities →</strong>
                    <span>
                      Challenges, activities & shared goals for your community.
                    </span>
                  </AudienceCard>
                </AudienceCTA>
              </HomeHeroCopy>
            </HeroLeft>

            <HeroRight>
              <div>
                <PhoneCarousel>
                  <PhoneMock>
                    <PhoneImage src={active.image} alt={active.header} />
                    {/**
                <PhoneScreen variant={active.variant}>
                  <div>
                    <PhoneHeader>
                      <span>{active.header}</span>
                      <PhoneChip>{active.chip}</PhoneChip>
                    </PhoneHeader>
                    {active.body()}
                  </div>
                  <PhoneFooter>{active.footer}</PhoneFooter>
                </PhoneScreen>
                */}
                  </PhoneMock>

                  <NavPrev onClick={goPrev} aria-label="Previous screen">
                    ‹
                  </NavPrev>
                  <NavNext onClick={goNext} aria-label="Next screen">
                    ›
                  </NavNext>
                </PhoneCarousel>

                <Dots>
                  {phoneScreens.map((screen, i) => (
                    <Dot
                      key={screen.id}
                      active={i === activeIndex}
                      onClick={() => setActiveIndex(i)}
                      aria-label={`Show ${screen.id} preview`}
                    />
                  ))}
                </Dots>
              </div>
            </HeroRight>
          </Hero>

          <StatsRow>
            <StatCard>
              <strong>Goals</strong>
              <span>Create challenges, habits, and routines</span>
            </StatCard>
            <StatCard>
              <strong>GPS</strong>
              <span>Track walks, runs, distance, and pace</span>
            </StatCard>
            <StatCard>
              <strong>Leaderboards</strong>
              <span>Turn progress into visible motivation</span>
            </StatCard>
            <StatCard>
              <strong>Community</strong>
              <span>Join circles and grow with others</span>
            </StatCard>
          </StatsRow>

          <SectionTitle>
            <h2>One Platform. Many Ways to Move Forward.</h2>
            <p>
              From personal goals and daily habits to challenges, activity
              tracking, leaderboards, and communities - Bealyo brings progress
              together in one experience.
            </p>
          </SectionTitle>

          <FeatureGrid>
            {[
              [
                "🎯",
                "Goals & Challenges",
                "Create personal goals, join challenges, and follow clear daily actions.",
              ],
              [
                "🏃",
                "GPS Activity Tracking",
                "Track walks and runs with distance, pace, time, and route history.",
              ],
              [
                "🥗",
                "Nutrition & Meals",
                "Manage meals, calories, hydration, and healthier daily choices.",
              ],
              [
                "✅",
                "Daily Tasks",
                "Break big goals into small actions you can complete every day.",
              ],
              [
                "🏆",
                "Live Leaderboards",
                "Make progress visible, create friendly competition, and keep people engaged.",
              ],
              [
                "👥",
                "Circles",
                "Join positive groups where people support, cheer, and grow together.",
              ],
            ].map(([icon, title, text]) => (
              <FeatureBox key={title}>
                <div>{icon}</div>
                <h3>{title}</h3>
                <p>{text}</p>
              </FeatureBox>
            ))}
          </FeatureGrid>

          <WhyRow>
            <WhyCard>
              <h3>Build Momentum</h3>
              <p>
                Turn goals into daily action through habits, challenges,
                routines, and small wins that keep you moving forward.
              </p>
            </WhyCard>

            <WhyCard>
              <h3>See Your Progress</h3>
              <p>
                Turn activity into visible progress - from workouts, steps, and
                daily tasks to streaks, achievements, and leaderboards.
              </p>
            </WhyCard>

            <WhyCard>
              <h3>Grow Together</h3>
              <p>
                Join a positive community where people encourage each other,
                celebrate progress, and stay accountable.
              </p>
            </WhyCard>
          </WhyRow>

          <StepsCard>
            <DownloadTitle>Experience Bealyo</DownloadTitle>
            <p>
              Bealyo is live and ready to use. Download the app, join a
              community, take on challenges, or start building your own
              progress.
            </p>
            <Steps>
              <li>
                <StepNumber>1</StepNumber>Download Bealyo on iPhone or Android
              </li>
              <li>
                <StepNumber>2</StepNumber>Create your free account
              </li>
              <li>
                <StepNumber>3</StepNumber>Choose goals, challenges, or habits
                that matter to you
              </li>
              <li>
                <StepNumber>4</StepNumber>Take small daily steps toward your
                best self
              </li>
            </Steps>

            <ButtonRow>
              <DownloadButton
                href={APP_STORE_URL}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src="/store-badges/app-store-badge.svg"
                  alt="Download on the App Store"
                />
              </DownloadButton>

              <DownloadButton
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src="/store-badges/google-play-badge.png"
                  alt="Get it on Google Play"
                  className="google-play-badge"
                />
              </DownloadButton>
            </ButtonRow>

            <SmallNote>
              Free to download. Start whenever you’re ready.
            </SmallNote>
          </StepsCard>

          <FinalCTA>
            <h2>Start Moving Forward with Bealyo</h2>

            <p>
              Whether you’re building better habits for yourself or creating a
              more active community, Bealyo gives you the tools to turn
              intention into action and progress.
            </p>

            <ButtonRow>
              <DownloadButton
                href={APP_STORE_URL}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src="/store-badges/app-store-badge.svg"
                  alt="Download on the App Store"
                />
              </DownloadButton>

              <DownloadButton
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src="/store-badges/google-play-badge.png"
                  alt="Get it on Google Play"
                />
              </DownloadButton>
            </ButtonRow>
          </FinalCTA>
        </>
      )}

      {activePage === "best" && (
        <InfoPage>
          <BestHero>
            <BestHeroCopy>
              <PageBadge>Be Your Best</PageBadge>

              <h1>Your Best Version Is Built One Day at a Time</h1>

              <p>
                Bealyo gives you one place to take action, track your progress,
                build better habits, and keep moving forward. You do not need to
                change everything at once. Start with one meaningful step and
                build from there.
              </p>

              <BestActions>
                <PrimaryAction
                  onClick={() => {
                    document.getElementById("best-download")?.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                    });
                  }}
                >
                  Start Your Journey
                </PrimaryAction>

                <SecondaryAction onClick={() => goToPage("community")}>
                  Explore Communities
                </SecondaryAction>
              </BestActions>
            </BestHeroCopy>

            <BestHeroVisual>
              <BestVisualText>
                <strong>Progress you can actually see</strong>

                <span>
                  Track your activity, distance, habits, health, streaks, and
                  daily wins in one personal journey.
                </span>
              </BestVisualText>
            </BestHeroVisual>
          </BestHero>

          <SectionTitle>
            <h2>Everything You Need to Keep Moving Forward</h2>

            <p>
              Choose what matters to you today. Bealyo helps you turn it into
              clear action, visible progress, and lasting momentum.
            </p>
          </SectionTitle>

          <InfoGrid>
            <InfoCard>
              <strong>🎯</strong>
              <h3>Choose Your Direction</h3>
              <p>
                Create meaningful activities, habits, challenges, and routines
                around fitness, health, mindset, nutrition, or personal growth.
              </p>
            </InfoCard>

            <InfoCard>
              <strong>🏃</strong>
              <h3>Take Real Action</h3>
              <p>
                Track walks, runs, workouts, daily tasks, check-ins, meals, and
                other actions that move you closer to the person you want to be.
              </p>
            </InfoCard>

            <InfoCard>
              <strong>📈</strong>
              <h3>See Your Progress</h3>
              <p>
                Follow your streaks, distance, steps, XP, achievements, and
                consistency so your effort never feels invisible.
              </p>
            </InfoCard>

            <InfoCard>
              <strong>⚡</strong>
              <h3>Reset and Keep Going</h3>
              <p>
                A missed day does not end your journey. Reconnect with what
                matters, take the next step, and build momentum again.
              </p>
            </InfoCard>
          </InfoGrid>

          <BestJourney>
            <PageBadge>Your Journey</PageBadge>

            <h2>Small Actions. Visible Progress. A Stronger You.</h2>

            <p>
              Bealyo is not about being perfect. It is about becoming more
              intentional, more consistent, and more connected to the life you
              want to build.
            </p>

            <JourneySteps>
              <JourneyCard>
                <span>1️⃣</span>
                <h3>Pick One Focus</h3>
                <p>
                  Choose one action, habit, or challenge that matters right now.
                </p>
              </JourneyCard>

              <JourneyCard>
                <span>2️⃣</span>
                <h3>Show Up Today</h3>
                <p>
                  Complete the next small step instead of waiting for perfect.
                </p>
              </JourneyCard>

              <JourneyCard>
                <span>3️⃣</span>
                <h3>Track the Win</h3>
                <p>
                  See your consistency, progress, streaks, and achievements.
                </p>
              </JourneyCard>

              <JourneyCard>
                <span>4️⃣</span>
                <h3>Keep Growing</h3>
                <p>
                  Build on your progress and choose your next meaningful step.
                </p>
              </JourneyCard>
            </JourneySteps>
          </BestJourney>

          <WhyRow>
            <WhyCard>
              <h3>Your Journey Is Personal</h3>
              <p>
                Use Bealyo privately and focus entirely on your own activities,
                habits, health, and progress.
              </p>
            </WhyCard>

            <WhyCard>
              <h3>Support Is There When You Need It</h3>
              <p>
                Join a circle, follow a challenge, or share progress without
                losing control of your individual journey.
              </p>
            </WhyCard>

            <WhyCard>
              <h3>Progress, Not Perfection</h3>
              <p>
                Every meaningful action counts. Bealyo helps you keep moving
                even when the journey is not perfectly smooth.
              </p>
            </WhyCard>
          </WhyRow>

          <FinalCTA id="best-download">
            <PageBadge>Start Today</PageBadge>

            <h2>Be the Best Version of Yourself</h2>

            <p>
              Your next step does not need to be huge. Choose something that
              matters, take action today, and let Bealyo help you build from
              there.
            </p>

            <ButtonRow>
              <DownloadButton
                href={APP_STORE_URL}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src="/store-badges/app-store-badge.svg"
                  alt="Download on the App Store"
                />
              </DownloadButton>

              <DownloadButton
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src="/store-badges/google-play-badge.png"
                  alt="Get it on Google Play"
                />
              </DownloadButton>
            </ButtonRow>

            <SmallNote>
              Free to download • Available for iPhone & Android
            </SmallNote>
          </FinalCTA>
        </InfoPage>
      )}
      {activePage === "community" && (
        <InfoPage>
          <PageHero>
            <PageBadge>FOR COMMUNITIES</PageBadge>

            <h1>
              Turn Your Community Into a{" "}
              <span style={{ color: "#49a6ff" }}>Movement</span>
            </h1>

            <p>
              Every community has the power to inspire change. Whether you're
              bringing together a family, a gym, a school, a workplace, or a
              group of friends, Bealyo helps you create meaningful challenges,
              celebrate progress, and build lasting habits - together.
            </p>
          </PageHero>

          <CommunityGrid>
            <CommunityCard>
              <CommunityImage
                src="/community/gym-studio.png"
                alt="Gyms & Studios"
              />

              <CommunityContent>
                <h3>Gyms & Studios</h3>

                <p>
                  Keep members engaged with challenges, live leaderboards, and
                  daily motivation that builds a stronger community.
                </p>

                <CommunityLink href="/gyms">
                  See Bealyo for gyms →
                </CommunityLink>
              </CommunityContent>
            </CommunityCard>

            <CommunityCard>
              <CommunityImage
                src="/community/trainers-coaches.png"
                alt="Trainers & Coaches"
              />
              <CommunityContent>
                <h3>Trainers & Coaches</h3>
                <p>
                  Support your trainees beyond every session with goals, habits,
                  activities, and progress they can follow every day.
                </p>
              </CommunityContent>
            </CommunityCard>

            <CommunityCard>
              <CommunityImage
                src="/community/families-friends.png"
                alt="Families & Friends"
              />
              <CommunityContent>
                <h3>Families & Friends</h3>
                <p>
                  Create healthy habits together, celebrate every win, and make
                  everyday progress more fun.
                </p>
              </CommunityContent>
            </CommunityCard>

            <CommunityCard>
              <CommunityImage
                src="/community/teams-workplaces.png"
                alt="Teams & Workplaces"
              />
              <CommunityContent>
                <h3>Teams & Workplaces</h3>
                <p>
                  Inspire healthier lifestyles through friendly challenges,
                  teamwork, and shared achievements.
                </p>
              </CommunityContent>
            </CommunityCard>
          </CommunityGrid>

          <PageBadge>Getting Started</PageBadge>
          <SectionHeader>
            <h2>Build Your Community in Minutes</h2>

            <p>
              It only takes a few minutes to build a community people love being
              part of.
            </p>
          </SectionHeader>

          <StepsGrid>
            <StepCard>
              <StepCircle>1</StepCircle>
              <h3>Download Bealyo</h3>
              <p>Download the app and create your free account.</p>
            </StepCard>

            <StepCard>
              <StepCircle>2</StepCircle>
              <h3>Create a Circle</h3>
              <p>Create a public or private circle for your community.</p>
            </StepCard>

            <StepCard>
              <StepCircle>3</StepCircle>
              <h3>Customize Your Circle</h3>
              <p>
                Add challenges, activities, GPS, AI, nutrition, leaderboards,
                and more.
              </p>
            </StepCard>

            <StepCard>
              <StepCircle>4</StepCircle>
              <h3>Grow Together</h3>
              <p>Invite your community and grow stronger together.</p>
            </StepCard>
          </StepsGrid>

          <FinalCTA>
            <PageBadge>Ready?</PageBadge>

            <h2>Your Community Starts Here</h2>

            <ButtonRow>
              <DownloadButton
                href={APP_STORE_URL}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src="/store-badges/app-store-badge.svg"
                  alt="Download on the App Store"
                />
              </DownloadButton>

              <DownloadButton
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src="/store-badges/google-play-badge.png"
                  alt="Get it on Google Play"
                />
              </DownloadButton>
            </ButtonRow>

            <SmallNote>
              Free to download • Available for iPhone & Android
            </SmallNote>
          </FinalCTA>
        </InfoPage>
      )}

      {activePage === "contact" && (
        <InfoPage>
          <PageHero>
            <PageBadge>Contact Bealyo</PageBadge>

            <h1>
              Let’s Build Something Great{" "}
              <span style={{ color: "#49a6ff" }}>Together</span>
            </h1>

            <p>
              Interested in Bealyo for your gym, health club, team, or
              community? Get in touch to explore a demo, pilot, or partnership.
            </p>
          </PageHero>

          <InfoGrid>
            <InfoCard>
              <strong>🚀</strong>
              <h3>Book a Demo</h3>
              <p>
                See how Bealyo can bring challenges, live leaderboards, and
                community engagement to your gym, club, team, or community.
              </p>
            </InfoCard>

            <InfoCard>
              <strong>📧</strong>
              <h3>Email</h3>
              <p>
                For demos, pilots, partnerships, support, or general questions,
                reach us directly at{" "}
                <a
                  href="mailto:support@bealyo.com"
                  style={{
                    color: "#ffffff",
                    fontWeight: 900,
                    textDecoration: "none",
                  }}
                >
                  support@bealyo.com
                </a>
                .
              </p>
            </InfoCard>

            <InfoCard>
              <strong>🏁</strong>
              <h3>Start with a Pilot</h3>
              <p>
                Want to see Bealyo in your own community? Start with a focused
                pilot and experience challenges, live leaderboards, and member
                engagement in action.
              </p>
            </InfoCard>

            <InfoCard>
              <strong>⚡</strong>
              <h3>Built Around Your Community</h3>
              <p>
                Every community is different. Bealyo can be shaped around your
                goals, challenges, members, and the experience you want to
                create.
              </p>
            </InfoCard>
          </InfoGrid>

          <FinalCTA>
            <h2>Let’s See What Bealyo Can Do for Your Community</h2>

            <p>
              Tell us what you’re building and what you want to achieve. We’ll
              explore together how Bealyo can fit your community.
            </p>

            <FooterLinks>
              <a href="mailto:support@bealyo.com">Email Us</a>

              <a
                href="https://instagram.com/bealyoversion"
                target="_blank"
                rel="noreferrer"
              >
                Instagram
              </a>

              <a
                href="https://x.com/bealyoversion"
                target="_blank"
                rel="noreferrer"
              >
                X
              </a>

              <a
                href="https://www.linkedin.com/company/bealyo/"
                target="_blank"
                rel="noreferrer"
              >
                LinkedIn
              </a>
            </FooterLinks>
          </FinalCTA>
        </InfoPage>
      )}

      <Feedback>
        <FooterTop>
          <FooterBrand>
            <img src="/logo.png" alt="Bealyo" />
            <p>Be the best version of yourself.</p>
          </FooterBrand>

          <FooterColumn>
            <h4>Explore</h4>

            <button onClick={() => goToPage("home")}>Home</button>
            <button onClick={() => goToPage("best")}>Be Your Best</button>
            <button onClick={() => goToPage("community")}>
              Start a Community
            </button>

            <button onClick={() => goToPage("contact")}>Contact</button>
          </FooterColumn>

          <FooterColumn>
            <h4>For Gyms</h4>
            <a href="/gyms">Gyms & Health Clubs</a>
            <button onClick={() => goToPage("contact")}>Book a Demo</button>
            <a href="/admin">{isAuthenticated ? "My Community" : "Log in"}</a>
          </FooterColumn>

          <FooterColumn>
            <h4>Connect</h4>
            <a href="mailto:support@bealyo.com">support@bealyo.com</a>
            <a
              href="https://www.linkedin.com/company/bealyo/"
              target="_blank"
              rel="noreferrer"
            >
              LinkedIn
            </a>
            <a
              href="https://instagram.com/bealyoversion"
              target="_blank"
              rel="noreferrer"
            >
              Instagram
            </a>
          </FooterColumn>
        </FooterTop>

        <FooterBottom>
          <span>© 2026 Bealyo. Be the best version of yourself.</span>
        </FooterBottom>
      </Feedback>

      {/** 
      <Feedback>
        <FooterTop>
          <FooterBrand>
            <img src="/logo.png" alt="Bealyo" />
            <p>Be the best version of yourself.</p>
          </FooterBrand>

          <FooterColumn>
            <h4>Product</h4>
            <button onClick={() => goToPage("home")}>Download</button>
            <button onClick={() => goToPage("how-to-join")}>How to Join</button>
            <button onClick={() => goToPage("tutorials")}>Tutorials</button>
          </FooterColumn>

          <FooterColumn>
            <h4>Community</h4>
            <button onClick={() => goToPage("leaders")}>For Leaders</button>
            <button onClick={() => goToPage("leaders")}>Circles</button>
            <button onClick={() => goToPage("leaders")}>Leaderboards</button>
          </FooterColumn>

          <FooterColumn>
            <h4>Contact</h4>

            <a href="mailto:support@bealyo.com">support@bealyo.com</a>

            <a
              href="https://instagram.com/bealyoversion"
              target="_blank"
              rel="noreferrer"
            >
              Instagram
            </a>

            <a
              href="https://x.com/bealyoversion"
              target="_blank"
              rel="noreferrer"
            >
              X
            </a>

            <a
              href="https://www.linkedin.com/company/bealyo/"
              target="_blank"
              rel="noreferrer"
            >
              LinkedIn
            </a>
          </FooterColumn>
        </FooterTop>

        <FooterBottom>
          <span>© 2026 Bealyo. Small actions. Better together.</span>
        </FooterBottom>
      </Feedback>
      */}
    </PageContainer>
  );
};

export default ProductHome;
